
import React from "react";
import { Capability, Schemes } from "../../../utils";
import { i18nManager } from "../../../i18n";
import MediaInput from "../MediaInput";
import { Elements } from "../Elements";
import { ConfirmDialog, Siren } from "../ConfirmDialog";
import LoadingGif from "../../../assets/images/loading_circle.gif";
import { ButtonManageRef, Checkbox, Classname, DOMHelper, Form, FormControl, ObjectHelper, Scheme, TextAreaInput, TextInput, TypeChecker } from "@rivtn/noseur";
import { BaseService, ConsusService, IMember, IProfileWebsite, IGallery, IUploadedFile, MemberService, SanitizedResponse } from "../../../services";
import { ChangePlan } from "../../sections/account_settings/plan_and_billing/ManagePlan";
import UpSellPlan from "../UpSellPlan";

export interface GalleryFormControl {
    name: string;
    type: string;
};

export interface GalleryProps {
    profile: IMember;
    uploadPath: string;
    createLabel: string;
    updateLabel: string;
    deleteLabel: string;
    capability?: string;
    noCompound?: boolean;
    enableAdLabel: string;
    onUnMount: () => void;
    fetchGallery: () => void;
    previewActionKey: string;
    previewCaptionKey: string;
    previewLocationKey: string;
    currentCapabilityValue?: any;
    updateSettings: (enableRivtnAd: boolean) => void;
    addGalleryMethod: (gallery: IGallery) => Promise<any>;
    updateGalleryMethod: (gallery: IGallery) => Promise<any>;
    deleteGalleryMethod: (gallery: IGallery) => Promise<any>;
    formControls: (GalleryFormControl | GalleryFormControl[])[];
    enableRivtnAdState: [boolean | undefined, React.Dispatch<React.SetStateAction<boolean | undefined>>];
    galleryState: [IGallery[] | undefined, React.Dispatch<React.SetStateAction<IGallery[] | undefined>>];
}

function Gallery(props: GalleryProps) {

    const now = new Date().getTime();
    const { profile, fetchGallery } = props;
    const [gallery, __] = props.galleryState;
    const consusService = ConsusService.getInstance();
    const [enableRivtnAd, _] = props.enableRivtnAdState;
    const labels = i18nManager.Labels.account.profile_website.gallery;
    const maxGallery = 7; // TODO FETCH FROM PLAN

    React.useEffect(() => {
        fetchGallery();

        return props.onUnMount;
    }, []);

    if (props.noCompound) return renderGallery();

    return (<div className="account-setting">
        <div className="header">
            <span className="title">{i18nManager.Labels.account.profile_website.title} - {labels.title}</span>
        </div>
        {renderGallery()}
    </div>);

    function renderGallery() {
        return ((!gallery)
            ? (<div style={{ margin: "90px 110px" }}>
                <img alt={"loading"} src={LoadingGif} style={{ width: 80, margin: "auto" }} />
            </div>)
            : (<Form scheme={Schemes.OMNEHOST} className={Classname.build("profile-settings", { "team-settings": props.noCompound })}>
                <Checkbox label={props.enableAdLabel} defaultChecked={enableRivtnAd} onChange={(e: any) => {
                    props.updateSettings(e.checked);
                }} />
                <hr />
                <div className="gallery-medias">
                    {gallery?.map((gallery) => {
                        return (<div key={gallery.external_id} className="gallery-media">
                            <div className="topper">
                                <i className="fa fa-pen" onClick={() => addOrUpdateGallery(gallery)} />
                                <i className="fa fa-trash" onClick={() => deleteGallery(gallery)} />
                            </div>
                            <img src={gallery.media_location + "?n=" + now} alt="gallery" onLoad={onMediaLoad} />
                            {((gallery as any)[props.previewActionKey] || (gallery as any)[props.previewLocationKey] || (gallery as any)[props.previewCaptionKey]) && (<div className="botommer">
                                {((gallery as any)[props.previewActionKey] || (gallery as any)[props.previewLocationKey])
                                    && (<div style={!(gallery as any)[props.previewCaptionKey] ? { marginBottom: 0 } : undefined}><span>{(gallery as any)[props.previewActionKey]}</span> {((gallery as any)[props.previewActionKey] && (gallery as any)[props.previewLocationKey]) && "-"} <span>{(gallery as any)[props.previewLocationKey]}</span></div>)}
                                <span className="lined-limit" style={{ "--maxTextLine": 3 } as any}>{(gallery as any)[props.previewCaptionKey]}</span>
                            </div>)}
                        </div>);
                    })}
                    <UpSellPlan type="shallow_icon" capability={props.capability} currentValue={props.currentCapabilityValue} childrenRelayProps={{ style: { opacity: 0.4 } }} >
                        {gallery.length < maxGallery && (<div className="gallery-media add" onClick={() => addOrUpdateGallery()}>
                            <i className="fa fa-plus" />
                        </div>)}
                    </UpSellPlan>
                </div>
            </Form>));
    }

    function onMediaLoad(e: any, isLast?: boolean) {
        const { width } = DOMHelper.getSizeRelativeToImageAspectRatio(e.target as HTMLImageElement, { height: 200 });
        const compoundWidth = DOMHelper.getElementWidth((e.target as any).parentElement.parentElement);
        if (width > (compoundWidth - (compoundWidth * 0.2))) {
            (e.target as any).parentElement.style.flex = `1`;
            return;
        }
        if (isLast) {
            (e.target as any).parentElement.style.flex = `unset`;
        }
        (e.target as any).parentElement.style.minWidth = `${width}px`;
    }

    function addOrUpdateGallery(gallery: IGallery = {} as any) {
        const dialogLabels = labels.dialog.add_update;
        const dialogErrorRef = { current: null } as any;
        const mediaInputManageRef = { current: undefined as any };

        function renderControl(_control: GalleryFormControl | GalleryFormControl[], index?: number): any {
            if (TypeChecker.isArray(_control)) {
                return (<Form scheme={Schemes.OMNEHOST} style={{ display: "flex", width: "100%", marginTop: 10 }}>
                    {(_control as GalleryFormControl[]).map(renderControl)}
                </Form>);
            }
            const control = _control as GalleryFormControl;
            if (control.type === "textbox") {
                return (<FormControl key={control.name} isFieldset label={(dialogLabels as any)[control.name]} {...Elements.FCFL} style={{ flex: 1, marginLeft: (index ? 20 : 0) }}>
                    <TextInput defaultValue={(gallery as any)[control.name]} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        (gallery as any)[control.name] = v;
                    }} />
                </FormControl>);
            } else if (control.type === "textarea") {
                return (<FormControl key={control.name} fill isFieldset label={(dialogLabels as any)[control.name]} {...Elements.FCFL} style={{ marginTop: 10 }}>
                    <TextAreaInput fill style={{ height: 180 }} defaultValue={(gallery as any)[control.name]} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        (gallery as any)[control.name] = v;
                    }} />
                </FormControl>);
            } else if (control.type === "file") {
                return (<MediaInput accepts="image/*,video/*" key={control.name} instruction={i18nManager.Labels.common.click_to_choose} style={{ alignSelf: "center" }}
                    default={(gallery as any)[control.name] ? (gallery as any)[control.name] + "?n=" + now : undefined}
                    manageRef={mediaInputManageRef} width={"100%"} height={300} maxFileSize={20000000}
                    onValidationFail={(_: File, error: string) => {
                        dialogErrorRef.current!.innerText = error;
                        return true;
                    }} />);
            }
            throw new Error("Unexpected GalleryFormControl type: " + control.type);
        }

        const dialog = ConfirmDialog({
            closable: true,
            title: (gallery.external_id ? props.updateLabel : props.createLabel),
            confirmLabel: (gallery.external_id ? dialogLabels.update : dialogLabels.add),
            style: { minWidth: (document?.documentElement?.clientWidth > 800 ? 560 : "95%"), maxWidth: 560 },
            content: (<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginTop: 15 }}>
                {props.formControls.map(renderControl)}
                <div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                const files = mediaInputManageRef.current?.files() as File[];
                button?.setLoadingState(true);
                const addOrUpdateGallery = (mediaLocation?: string) => {
                    if (mediaLocation) {
                        gallery.media_location = mediaLocation;
                    }
                    const method = (gallery.external_id ? props.updateGalleryMethod : props.addGalleryMethod);
                    method(gallery).then(() => {
                        Siren.alert(gallery.external_id ? dialogLabels.update_successful : dialogLabels.add_successful);
                        dialog.hide();
                        fetchGallery();
                    }).catch((error: any) => {
                        dialogErrorRef.current!.innerText = error.errorMessage;
                        BaseService.reportError(error);
                        button?.setLoadingState(false);
                    });
                }
                if (files.length) {
                    const file = files[0];
                    const fileName = (gallery.external_id && gallery.media_location ? gallery.media_location.substring(gallery.media_location.lastIndexOf("/") + 1) : `${profile.janus_user_external_id}_${file.name.substring(file.name.length - 10, file.name.length)}`);
                    consusService.uploadFile(props.uploadPath, fileName, file).then(async ({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                        addOrUpdateGallery(sanitized.consus_download_url);
                    }).catch((error: any) => {
                        dialogErrorRef.current!.innerText = error.errorMessage;
                        BaseService.reportError(error);
                        button?.setLoadingState(false);
                    });
                } else {
                    if (!gallery.media_location) {
                        dialogErrorRef.current!.innerText = dialogLabels.no_media_selected;
                        button?.setLoadingState(false);
                        return false;
                    }
                    addOrUpdateGallery();
                }
                return false;
            }
        });
        dialog.show();
    }

    function deleteGallery(gallery: IGallery) {
        const dialogLabels = labels.dialog.delete;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            title: props.deleteLabel,
            confirmScheme: Scheme.DANGER,
            style: { maxWidth: 500 },
            desc: ObjectHelper.expandStringTemplate(dialogLabels.desc, {
                name: gallery.media_location.substring(gallery.media_location.lastIndexOf("/") + 1),
            }, { chop: "$" }),
            content: (<div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>),
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                dialogErrorRef.current!.innerText = " ";
                const removeFromGallery = () => {
                    props.deleteGalleryMethod(gallery).then(() => {
                        Siren.alert(dialogLabels.successful);
                        dialog.hide();
                        fetchGallery();
                    }).catch((error: any) => {
                        dialogErrorRef.current!.innerText = error.errorMessage;
                        BaseService.reportError(error);
                        button?.setLoadingState(false);
                    });
                };
                consusService.deleteFile([gallery.media_location]).then(removeFromGallery).catch(removeFromGallery);
                return false;
            }
        });
        dialog.show();
    }

}

export default Gallery;
